import { cn } from '@askable/ui/lib/utils';
import { createContext, useContext, useId, useMemo, useState } from 'react';

import type { ReactNode } from 'react';

export type LockedPopoverSide = 'right' | 'left';

type LockedPopoverGroupValue = {
  openId: string | null;
  toggle: (id: string) => void;
  close: () => void;
};

const LockedPopoverGroupContext = createContext<LockedPopoverGroupValue | null>(null);

/**
 * Coordinates a set of locked popovers so only one stays open at a time.
 * Wrap the menu content; each popover toggles/closes via {@link useLockedPopover}
 * (single instance) or {@link useLockedPopoverGroup} (keyed items in a list).
 */
export const LockedPopoverGroup = ({ children }: { children: ReactNode }) => {
  const [openId, setOpenId] = useState<string | null>(null);
  const value = useMemo<LockedPopoverGroupValue>(
    () => ({
      openId,
      toggle: id => setOpenId(prev => (prev === id ? null : id)),
      close: () => setOpenId(null),
    }),
    [openId],
  );

  return <LockedPopoverGroupContext.Provider value={value}>{children}</LockedPopoverGroupContext.Provider>;
};

export const useLockedPopoverGroup = () => {
  const group = useContext(LockedPopoverGroupContext);
  if (!group) {
    throw new Error('LockedPopover components must be rendered within a LockedPopoverGroup');
  }
  return group;
};

/** Open-state for a single locked popover, keyed by an auto-generated id. */
export const useLockedPopover = () => {
  const group = useLockedPopoverGroup();
  const id = useId();
  return {
    isOpen: group.openId === id,
    toggle: () => group.toggle(id),
    close: group.close,
  };
};

type Props = {
  children: ReactNode;
  isOpen: boolean;
  side?: LockedPopoverSide;
  className?: string;
};

/**
 * Floating explanation panel for locked create-study menu items.
 *
 * Render it as a sibling of the trigger inside a `group relative` element.
 * Hover/focus is handled purely by CSS (`group-hover` / `group-focus-within`)
 * and `isOpen` drives click/touch toggling — no Radix, so there's no
 * hover/controlled-state fighting that would make the popover flicker.
 */
export const LockedPopover = ({ children, isOpen, side = 'right', className }: Props) => (
  <div
    className={cn(
      `pointer-events-none absolute left-3 top-1/2 z-50 -translate-y-[2rem] cursor-auto rounded-md border border-border bg-popover text-start text-popover-foreground shadow-md transition-opacity sm:left-auto sm:right-0 sm:translate-x-full lg:group-focus-within:pointer-events-auto lg:group-focus-within:opacity-100 lg:group-hover:pointer-events-auto lg:group-hover:opacity-100`,
      {
        'translate-y-6 sm:left-0 sm:right-auto sm:-translate-x-full sm:-translate-y-[2rem]': side === 'left',
        'pointer-events-auto opacity-100': isOpen,
        'opacity-0': !isOpen,
      },
      className,
    )}
    onClick={e => {
      e.preventDefault();
      e.stopPropagation();
    }}
  >
    {children}
  </div>
);
